import { Box, Divider, List, ListSubheader, styled } from '@mui/material';
import { filterVisibleMenuItems } from 'helpers/layout/sidebarMenu';
import type { MenuItemType } from 'helpers/layout/sidebarMenu';
import { SidebarMenuItemType } from 'hooks/ui/useSidebarMenuItems';
import { useGetAllPermissionsList } from 'hooks/ui/usePermissions';
import { useGetRole } from 'hooks/ui/useGetRole';
import NavigationItem from './NavigationItem';

enum VisibleEntryKind {
  Section = 'section',
  Item = 'item',
}

type VisibleSectionEntry = {
  kind: VisibleEntryKind.Section;
  name: string;
  visibleChildren: Array<MenuItemType>;
};

type VisibleItemEntry = {
  kind: VisibleEntryKind.Item;
  item: SidebarMenuItemType;
};

type VisibleEntry = VisibleSectionEntry | VisibleItemEntry;

const NavigationHeader = styled(ListSubheader)(({ theme }) => ({
  color: theme.palette.text.secondary,
  backgroundColor: theme.palette.background.default,
  fontSize: theme.fontSizes.sm,
  lineHeight: '100%',
  padding: theme.spacing(theme.spacingSizes.xs, theme.spacingSizes.sm),
  textTransform: 'uppercase',
  fontWeight: theme.typography.fontWeightRegular,
}));

const NavigationDivider = styled(Divider)(({ theme }) => ({
  margin: theme.spacing(theme.spacingSizes.xs, 0),
}));

type Props = {
  menuItems: Array<SidebarMenuItemType>;
};

const Navigation: React.FC<Props> = ({ menuItems }) => {
  const permissionsList = useGetAllPermissionsList();
  const { roleSlug } = useGetRole();

  const visibleEntries = menuItems
    .map((item) => {
      if (item.type === 'section') {
        const visibleChildren = filterVisibleMenuItems(
          item.children ?? [],
          permissionsList,
          roleSlug,
        );
        if (!visibleChildren.length) {
          return null;
        }
        return {
          kind: VisibleEntryKind.Section,
          name: item.name,
          visibleChildren,
        };
      }
      return { kind: VisibleEntryKind.Item, item };
    })
    .filter((entry): entry is VisibleEntry => entry !== null);

  return (
    <Box>
      {visibleEntries.map((entry, index) => {
        if (entry.kind === VisibleEntryKind.Section) {
          return (
            <List
              key={entry.name}
              subheader={
                <NavigationHeader disableSticky>{entry.name}</NavigationHeader>
              }
            >
              {entry.visibleChildren.map((child) => (
                <NavigationItem key={child.name} {...child} />
              ))}
              {index !== visibleEntries.length - 1 && <NavigationDivider />}
            </List>
          );
        }
        return (
          <List key={entry.item.name}>
            <NavigationItem key={entry.item.name} {...entry.item} />
          </List>
        );
      })}
    </Box>
  );
};

export default Navigation;
